import { createSlice } from '@reduxjs/toolkit';
// utils
import { createUserUpp } from '../../services/user/createUserUpp';
import { createOrg } from '../../services/register/createOrg';
import { addUserToOrg } from '../../services/register/addUserToOrg';
import axios from '../../utils/axios';
import { updateOrgApi } from '../../services/register/updateOrgApi';
import { chatAppOrgRegister } from '../../services/register/chatAppOrgRegister';
import { extractPhoneCode } from '../../components/hook-form';

const initialState = {
  isLoading: false,
  error: null,
  customers: null, // customers === orgs from api
  paginatedCustomers: { data: [], pagination: null },
  initialized: false,
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET CUSTOMERS SUCCESS
    getCustomersSuccess(state, action) {
      const orgs = action.payload;
      state.isLoading = false;
      state.initialized = true;
      state.customers = orgs;
    },
    // GET CUSTOMER UPDATE
    getCustomerUpdate(state, action) {
      state.isLoading = false;
      state.initialized = true;
    },

    // GET PAGINATED CUSTOMERS SUCCESS
    getPaginatedCustomersSuccess(state, action) {
      state.isLoading = false;
      state.initialized = true;
      state.paginatedCustomers = action.payload;
    },
  },
});

// Actions
export const { getCustomersSuccess, getCustomerUpdate } = customersSlice.actions;

// Reducer
export default customersSlice.reducer;

export const getAllCustomers = (state) => state.customers;

export const getPaginatedCustomers = (state) => state.customers.paginatedCustomers;

export function getCustomers(page, pageSize, search, startDate, endDate, partner) {
  return async (dispatch) => {
    dispatch(customersSlice.actions.startLoading());
    try {
      const partnerId = JSON.parse(localStorage.getItem('currentPartner'))?._id;
      const params = {};

      if (search) {
        params.search = search;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }
      if (partner && partner.length > 0) {
        params.partner = Array.isArray(partner) ? partner.join(',') : partner;
      }

      const response = await axios({
        method: 'get',
        url: `/partners/${partnerId}/get-all-organizations`,
        params,
      });
      dispatch(customersSlice.actions.getCustomersSuccess(response.data));
    } catch (error) {
      dispatch(customersSlice.actions.hasError(error));
    }
  };
}

export function getCustomersPaginated(
  page = 1,
  perPage = 25,
  search,
  startDate,
  endDate,
  partner,
  status
) {
  return async (dispatch) => {
    dispatch(customersSlice.actions.startLoading());
    try {
      const partnerId = JSON.parse(localStorage.getItem('currentPartner'))?._id;
      const params = { page, perPage };

      if (search) {
        params.search = search;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }
      if (partner && partner.length > 0) {
        params.partner = Array.isArray(partner) ? partner.join(',') : partner;
      }
      if (status !== undefined && status !== 'all') {
        params.status = status;
      }

      const response = await axios({
        method: 'get',
        url: `/partners/${partnerId}/get-all-organizations-paginated`,
        params,
      });
      // response.data === { data: [...orgs], pagination: {...} }
      dispatch(customersSlice.actions.getPaginatedCustomersSuccess(response.data));
    } catch (error) {
      dispatch(customersSlice.actions.hasError(error));
    }
  };
}

export function changeStatus(status, _id) {
  return async (dispatch) => {
    dispatch(customersSlice.actions.startLoading());
    try {
      // updating org
      const orgFormData = new FormData();
      orgFormData.append('status', !status);

      await updateOrgApi(orgFormData, _id);

      dispatch(customersSlice.actions.getCustomerUpdate());
    } catch (error) {
      dispatch(customersSlice.actions.hasError(error));
    }
  };
}

export function addCustomer(data) {
  return async (dispatch) => {
    dispatch(customersSlice.actions.startLoading());
    try {
      // creating user (POST /users — keeps parent admin session; do not use /auth/register)
      const user = await createUserUpp({
        full_name: data?.userName,
        email: data.email,
        country_code: extractPhoneCode(data.country),
        phone: data.phoneNumber,
        password: data.password,
      });

      // creating org
      const orgFormData = new FormData();
      orgFormData.append('name', data.name);
      orgFormData.append('email', data.email);
      orgFormData.append('phone', data.phoneNumber);
      orgFormData.append('country_code', extractPhoneCode(data.country));
      orgFormData.append('partner', JSON.parse(localStorage.getItem('currentPartner'))?._id);

      const org = await createOrg(orgFormData, user._id);

      // adding user to org
      const obj = {
        user: user._id,
        partner: JSON.parse(localStorage.getItem('currentPartner'))?._id,
        role: 'admin',
      };
      await addUserToOrg(obj, org._id);

      await chatAppOrgRegisterHelper(org, user);

      dispatch(customersSlice.actions.getCustomerUpdate());
    } catch (error) {
      dispatch(customersSlice.actions.hasError(error));
      throw new Error(error?.message);
    }
  };
}

const chatAppOrgRegisterHelper = async (org, user) => {
  const chatApp = await chatAppOrgRegister(org, user);
  return chatApp;
};

export function updateCustomer(data, currentCustomer) {
  return async (dispatch) => {
    dispatch(customersSlice.actions.startLoading());
    try {
      // updating user
      // const userFormData = new FormData();
      // userFormData.append('full_name', data?.userName);
      // userFormData.append('email', data.email);
      // userFormData.append('country_code', data.country);
      // userFormData.append('phone', data.phoneNumber);

      // const user = await updateUser(userFormData, currentUser?._id);

      // updating org
      const orgFormData = new FormData();
      orgFormData.append('name', data.name);
      orgFormData.append('email', data.email);
      orgFormData.append('phone', data.phoneNumber);
      if (data.country) {
        orgFormData.append('country_code', extractPhoneCode(data.country));
      }
      orgFormData.append('city', data.location);

      await updateOrgApi(orgFormData, currentCustomer?._id);

      dispatch(customersSlice.actions.getCustomerUpdate());
    } catch (error) {
      dispatch(customersSlice.actions.hasError(error));
      throw new Error(error?.message);
    }
  };
}

export function resetPassword(password, userId) {
  return async () => {
    const formData = new FormData();
    formData.append('password', password);
    try {
      await axios({
        method: 'put',
        url: `/users/${userId}`,
        data: formData,
      });
    } catch (error) {
      throw new Error(error?.message);
    }
  };
}

export function manageAccount(data) {
  return async () => {
    try {
      const params = {
        user: data?.userId,
        organization: data?.orgId,
      };
      const loginUserId =
        typeof window !== 'undefined' ? localStorage.getItem('loginUserId') : null;
      if (params.organization && loginUserId) {
        params.superAdminUserId = loginUserId;
      }
      const response = await axios({
        method: 'get',
        url: `/auth/get-access-token`,
        params,
      });
      const { accessToken } = response.data;

      return { accessToken };
    } catch (error) {
      throw new Error(error?.message);
    }
  };
}

export function getUserAccessToken(data) {
  return async () => {
    try {
      const params = {
        user: data?.userId,
      };

      const response = await axios({
        method: 'get',
        url: `/auth/get-user-access-token`,
        params,
      });
      const { accessToken } = response.data;

      return { accessToken };
    } catch (error) {
      throw new Error(error?.message);
    }
  };
}

export function updateAccManager(data) {
  return async (dispatch) => {
    try {
      const response = await axios({
        method: 'post',
        url: `/lms_user/api/account_manager`,
        params: {
          org_id: data?.orgId,
          users: data?.users,
          primary: data?.primaryAm,
        },
      });
      dispatch(customersSlice.actions.getCustomersSuccess(response.data));
      return response.data?.find((org) => org?.org_id === data?.orgId);
    } catch (error) {
      throw new Error(Object.keys(error.errors).map((err) => error.errors[err]));
    }
  };
}

export function transferToPartner(data) {
  return async (dispatch) => {
    try {
      dispatch(customersSlice.actions.startLoading());
      await axios({
        method: 'put',
        url: `/organizations/transfer-to-partner`,
        data: {
          organization: data?.orgID,
          partner: data?.partnerID,
        },
      });
      dispatch(customersSlice.actions.getCustomerUpdate());
    } catch (error) {
      throw new Error(error?.message);
    }
  };
}

export function moveOrgToBin(data) {
  return async (dispatch) => {
    try {
      dispatch(customersSlice.actions.startLoading());
      const response = await axios({
        method: 'delete',
        url: `/lms_user/api/org/${data?.orgID}`,
      });
      // console.log(response.data);
      dispatch(customersSlice.actions.getCustomersSuccess(response.data));
    } catch (error) {
      throw new Error(Object.keys(error.errors).map((err) => error.errors[err]));
    }
  };
}

export function restoreOrgFromBin(data) {
  return async (dispatch) => {
    try {
      dispatch(customersSlice.actions.startLoading());
      const response = await axios({
        method: 'post',
        url: `/lms_user/api/org/${data?.orgID}/restore`,
      });
      dispatch(customersSlice.actions.getCustomersSuccess(response.data));
    } catch (error) {
      throw new Error(Object.keys(error.errors).map((err) => error.errors[err]));
    }
  };
}
